// Validatie en typering van de omgevingsvariabelen uit het bouwcontract.
//
// BELANGRIJK: deze module leest zelf géén .env-bestand en importeert dotenv
// niet. Het laden van .env is de verantwoordelijkheid van de aanroeper — de
// API doet dat in haar bootstrap, Next.js en Vite doen het uit zichzelf, en in
// tests wordt een expliciet object doorgegeven. Zo blijft deze module puur en
// bruikbaar in een browserbundel of in een test zonder bestandssysteem.

import { z } from 'zod'
import {
  DEFAULT_BASE_URLS,
  DEFAULT_DATABASE_POOL_MAX,
  DEFAULT_LOG_LEVEL,
  DEFAULT_PORTS,
  DEFAULT_MAIL_DRIVER,
  DEFAULT_MAIL_FROM,
  DEFAULT_MAIL_FROM_NAME,
  DEFAULT_MESSAGE_QUEUE_MINUTES,
  DEFAULT_SMTP_HOST,
  MAIL_DRIVERS,
  DEFAULT_CHANNEL_IMPORT_MINUTES,
  DEFAULT_SMTP_PORT,
  DEFAULT_STORAGE_LOCAL_ROOT,
  LOG_LEVELS,
  NODE_ENVS,
  STORAGE_DRIVERS,
} from './constants'
import {
  SOURCE_LOCALE,
  SUPPORTED_LOCALES as PLATFORM_LOCALES,
  isLocale,
  type Locale,
} from './locales'

/** Poortnummer: uit de omgeving komt altijd een string, dus coercen. */
function portSchema(defaultPort: number) {
  const message = 'must be a port number between 1 and 65535'
  return z.coerce
    .number({ error: message })
    .int({ error: message })
    .min(1, { error: message })
    .max(65535, { error: message })
    .default(defaultPort)
}

/**
 * Een interval in minuten. Nul is toegestaan en betekent uit — dat is geen
 * bijzondere waarde die apart afgehandeld moet worden maar de gewone manier om
 * een periodieke taak stil te zetten, en op een ontwikkelmachine is dat de
 * normale stand.
 */
function intervalSchema(defaultMinutes: number) {
  const message = 'must be a whole number of minutes between 0 and 1440'
  return z.coerce
    .number({ error: message })
    .int({ error: message })
    .min(0, { error: message })
    .max(1440, { error: message })
    .default(defaultMinutes)
}

/** Absolute http(s)-URL zonder afsluitende slash-eis. */
function httpUrlSchema(defaultUrl: string) {
  return z
    .url({ error: 'must be a valid absolute URL' })
    .refine((value) => value.startsWith('http://') || value.startsWith('https://'), {
      error: 'must use the http:// or https:// protocol',
    })
    .default(defaultUrl)
}

/**
 * PostgreSQL-verbindingsstring. Bewust geen z.url(): een connection string is
 * URL-achtig maar wordt beoordeeld op protocol, host en databasenaam.
 */
function isPostgresConnectionString(value: string): boolean {
  let parsed: URL
  try {
    parsed = new URL(value)
  } catch {
    return false
  }
  if (parsed.protocol !== 'postgres:' && parsed.protocol !== 'postgresql:') {
    return false
  }
  if (parsed.hostname === '') {
    return false
  }
  // Het pad bevat de databasenaam; zonder databasenaam is de string onbruikbaar.
  return parsed.pathname.replace(/^\/+/, '') !== ''
}

const databaseUrlSchema = z
  .string({ error: 'must be a postgres connection string' })
  .min(1, { error: 'must not be empty' })
  .refine(isPostgresConnectionString, {
    error: 'must be a postgres:// or postgresql:// URL including a database name',
  })

/**
 * SUPPORTED_LOCALES komt als kommagescheiden string binnen en wordt een lijst
 * van talen: duplicaten verdwijnen, de volgorde uit de omgeving blijft staan.
 */
const supportedLocalesSchema = z
  .string({ error: 'must be a comma-separated list of locales' })
  .default(PLATFORM_LOCALES.join(','))
  .transform((raw, ctx): readonly Locale[] => {
    const parts = raw
      .split(',')
      .map((part) => part.trim().toLowerCase())
      .filter((part) => part !== '')

    if (parts.length === 0) {
      ctx.addIssue({ code: 'custom', message: 'must list at least one locale' })
      return z.NEVER
    }

    const unsupported = parts.filter((part) => !isLocale(part))
    if (unsupported.length > 0) {
      ctx.addIssue({
        code: 'custom',
        message: `contains unsupported locale(s) ${unsupported.join(', ')}; the platform supports ${PLATFORM_LOCALES.join(', ')}`,
      })
      return z.NEVER
    }

    const seen = new Set<string>()
    const locales: Locale[] = []
    for (const part of parts) {
      if (isLocale(part) && !seen.has(part)) {
        seen.add(part)
        locales.push(part)
      }
    }
    return locales
  })

/**
 * Het canonieke schema. De namen komen letterlijk uit het bouwcontract; wie ze
 * anders spelt, breekt een andere applicatie.
 */
export const envSchema = z
  .object({
    // Bewust GEEN standaardwaarde. Met een default op 'development' krijgt een
    // uitrol die NODE_ENV vergeet stilzwijgend ontwikkelgedrag: main.ts kaatst dan
    // elke origin terug én stuurt credentials mee, waarmee elke website namens een
    // ingelogde gebruiker kan praten met de API. Liever weigeren op te starten.
    NODE_ENV: z.enum(NODE_ENVS, { error: `must be one of ${NODE_ENVS.join(', ')}` }),

    /**

     * Adres waarop de API luistert.

     *

     * In ontwikkeling 0.0.0.0, zodat het platform vanaf een telefoon of een tweede

     * machine bereikbaar is (§17.12, ADR-032). In productie staat de API achter een

     * reverse proxy en hoort hij alleen op de loopback te luisteren: rechtstreeks

     * benaderbaar zijn op een publiek adres omzeilt TLS en de proxyregels.

     */

    API_HOST: z.string().min(1).default('0.0.0.0'),

    API_PORT: portSchema(DEFAULT_PORTS.api),
    API_BASE_URL: httpUrlSchema(DEFAULT_BASE_URLS.api),
    /**
     * Het adres waarop de API van BUITEN bereikbaar is.
     *
     * Los van `API_BASE_URL`, omdat die twee in productie verschillen en één
     * variabele voor allebei aantoonbaar fout gaat. `API_BASE_URL` is waar de
     * server zelf naartoe belt — een loopbackadres, want dat is sneller en het
     * hoeft niet langs de reverse proxy. Deze is wat er in URL's belandt die een
     * ander moet kunnen openen: de foto's in de gids en op de gastensite, en de
     * iCal-feeds die Airbnb en Booking.com ophalen.
     *
     * Stond dit er niet, dan kregen die feeds `http://127.0.0.1:4311/ical/...`
     * mee. Dat is een adres dat op elke andere machine naar niets wijst, en het
     * mislukt stilzwijgend: een kanaal dat de agenda niet kan ophalen, meldt dat
     * niet bij ons.
     *
     * Leeg laten betekent: gelijk aan `API_BASE_URL`. Lokaal is dat juist.
     */
    API_PUBLIC_URL: z.string().trim().default(''),
    PUBLIC_BASE_URL: httpUrlSchema(DEFAULT_BASE_URLS['web-public']),
    DASHBOARD_BASE_URL: httpUrlSchema(DEFAULT_BASE_URLS['web-dashboard']),
    /**
     * De beheeromgeving (§17.38). Staat hier omdat de API hem in productie op de
     * CORS-lijst moet zetten: zonder dat kan de beheerder wel inloggen op zijn eigen
     * origin, maar bereikt geen enkele aanroep de API.
     */
    ADMIN_BASE_URL: httpUrlSchema(DEFAULT_BASE_URLS['web-admin']),

    // Geen standaardwaarde: een verkeerd geraden database is erger dan een
    // duidelijke foutmelding bij het opstarten.

    DATABASE_URL: databaseUrlSchema,
    /**
     * Verbinding waarmee migraties, seed en bootstrap draaien: de eigenaar van het
     * schema. De applicatie gebruikt DATABASE_URL met een rol die RLS niet omzeilt
     * (§17.3). Ontbreekt deze, dan valt alles terug op DATABASE_URL — handig lokaal,
     * ongewenst in productie.
     */
    DATABASE_ADMIN_URL: z.string().min(1).optional(),
    DATABASE_POOL_MAX: z.coerce
      .number({ error: 'must be a whole number of connections' })
      .int({ error: 'must be a whole number of connections' })
      .min(1, { error: 'must be at least 1' })
      .max(100, { error: 'must be at most 100' })
      .default(DEFAULT_DATABASE_POOL_MAX),

    STORAGE_DRIVER: z
      .enum(STORAGE_DRIVERS, { error: `must be one of ${STORAGE_DRIVERS.join(', ')}` })
      .default('local'),
    STORAGE_LOCAL_ROOT: z
      .string({ error: 'must be a path' })
      .min(1, { error: 'must not be empty' })
      .default(DEFAULT_STORAGE_LOCAL_ROOT),

    DEFAULT_LOCALE: z
      .enum(PLATFORM_LOCALES, { error: `must be one of ${PLATFORM_LOCALES.join(', ')}` })
      .default(SOURCE_LOCALE),
    SUPPORTED_LOCALES: supportedLocalesSchema,

    SMTP_HOST: z
      .string({ error: 'must be a hostname' })
      .min(1, { error: 'must not be empty' })
      .default(DEFAULT_SMTP_HOST),
    SMTP_PORT: portSchema(DEFAULT_SMTP_PORT),
    /** Leeg bij mailpit, dat elke login accepteert. */
    SMTP_USER: z.string().default(''),
    SMTP_PASSWORD: z.string().default(''),

    /**
     * Welke bezorgdriver actief is.
     *
     * Geen stilzwijgende terugval van `smtp` naar `log`: een platform dat bij een
     * onbereikbare mailserver ongemerkt naar de log gaat schrijven, meldt niets en
     * verliest alles (§17.31).
     */
    MAIL_DRIVER: z
      .enum(MAIL_DRIVERS, { error: `must be one of ${MAIL_DRIVERS.join(', ')}` })
      .default(DEFAULT_MAIL_DRIVER),

    /**
     * Microsoft Graph, alleen nodig bij `MAIL_DRIVER=graph` (§17.46).
     *
     * Geen standaardwaarden en geen validatie op vorm: leeg is geldig zolang de
     * driver niet `graph` is, en de driver zelf weigert te starten wanneer er iets
     * ontbreekt. Dat is de plek waar die controle hoort — daar staat ook wat er in
     * Azure moet zijn geregeld.
     */
    MAIL_GRAPH_TENANT_ID: z.string().trim().default(''),
    MAIL_GRAPH_CLIENT_ID: z.string().trim().default(''),
    MAIL_GRAPH_CLIENT_SECRET: z.string().default(''),
    /** Leeg betekent: dezelfde postbus als MAIL_FROM. */
    MAIL_GRAPH_SENDER: z.string().trim().default(''),
    /** Het afzenderadres van het platform; hierop wordt SPF en DKIM ingericht. */
    MAIL_FROM: z
      .string({ error: 'must be an email address' })
      .min(3, { error: 'must not be empty' })
      .default(DEFAULT_MAIL_FROM),
    MAIL_FROM_NAME: z.string().default(DEFAULT_MAIL_FROM_NAME),

    /** Hoe vaak de berichtenwachtrij wordt geleegd, in minuten. 0 zet hem uit. */
    MESSAGE_QUEUE_MINUTES: z.coerce
      .number({ error: 'must be a number' })
      .int({ error: 'must be a whole number' })
      .min(0, { error: 'must not be negative' })
      .max(60, { error: 'must be at most 60' })
      .default(DEFAULT_MESSAGE_QUEUE_MINUTES),

    LOG_LEVEL: z
      .enum(LOG_LEVELS, { error: `must be one of ${LOG_LEVELS.join(', ')}` })
      .default(DEFAULT_LOG_LEVEL),

    /**
     * Hoe vaak de kanaalfeeds worden opgehaald, in minuten.
     *
     * Configureerbaar en niet vast, omdat het antwoord per omgeving verschilt:
     * op een ontwikkelmachine wil je hem uit kunnen zetten (0), en de kanalen
     * zelf ververst hun feed hooguit elk kwartier — vaker ophalen levert dan geen
     * verse gegevens op maar wel verkeer waar het kanaal iets van kan vinden.
     * Vijftien minuten is de afspraak uit hoofdstuk 19: "binnen enkele minuten".
     */
    CHANNEL_IMPORT_INTERVAL_MINUTES: intervalSchema(DEFAULT_CHANNEL_IMPORT_MINUTES),

    /**
     * Sleutels waarmee providergeheimen van verhuurders worden versleuteld.
     *
     * Vorm: `id:base64sleutel`, meerdere gescheiden door een komma; de eerste
     * versleutelt, de rest kan nog ontcijferen. Leeg betekent: er kan geen
     * betaalkoppeling worden opgeslagen — de rest van het platform draait gewoon
     * door, want handmatig betalen heeft geen provider nodig (regel 9).
     *
     * Bewust géén standaardwaarde. Een ingebakken sleutel is geen sleutel; hij zou
     * in elke installatie dezelfde zijn en in deze repository staan.
     */
    PAYMENT_CREDENTIAL_KEYS: z.string({ error: 'must be a string' }).default(''),
  })
  .superRefine((env, ctx) => {
    // De standaardtaal moet daadwerkelijk uitgeleverd kunnen worden.
    if (!env.SUPPORTED_LOCALES.includes(env.DEFAULT_LOCALE)) {
      ctx.addIssue({
        code: 'custom',
        path: ['DEFAULT_LOCALE'],
        message: `must be part of SUPPORTED_LOCALES (currently ${env.SUPPORTED_LOCALES.join(', ')})`,
      })
    }
  })

/** De gevalideerde, getypeerde omgeving. */
export type Env = z.infer<typeof envSchema>

/** Eén variabele met alles wat er aan mankeert. */
export interface EnvProblem {
  readonly variable: string
  readonly messages: readonly string[]
}

/**
 * Structurele vorm van een zod-issue. Bewust niet het geëxporteerde zod-type:
 * dit houdt de foutopmaak los van de interne typen van de bibliotheek.
 */
interface RawIssue {
  readonly code: string
  readonly path: ReadonlyArray<PropertyKey>
  readonly message: string
}

function formatProblems(problems: readonly EnvProblem[]): string {
  const lines = problems.map((problem) => `  - ${problem.variable}: ${problem.messages.join('; ')}`)
  return ['Invalid environment configuration:', ...lines].join('\n')
}

/**
 * Fout bij een ongeldige omgeving. De boodschap is per variabele gegroepeerd en
 * leesbaar; `problems` is beschikbaar voor wie er programmatisch iets mee wil.
 */
export class EnvValidationError extends Error {
  readonly problems: readonly EnvProblem[]

  constructor(problems: readonly EnvProblem[]) {
    super(formatProblems(problems))
    this.name = 'EnvValidationError'
    this.problems = problems
  }
}

/**
 * De ruwe omgeving als sleutel-waardepaar.
 *
 * Bewust géén `NodeJS.ProcessEnv`: dit package wordt ook door de browserbundels
 * geïmporteerd, en een verwijzing naar de Node-typen dwingt daar `@types/node`
 * af voor code die alleen een `HealthResponse` nodig heeft. De aanroeper geeft
 * de bron expliciet mee — in Node is dat `process.env`.
 */
export type EnvSource = Readonly<Record<string, string | undefined>>

/** Zet één zod-issue om in een zin over die ene variabele. */
function describeIssue(issue: RawIssue, variable: string, source: EnvSource): string {
  const raw = variable === '' ? undefined : source[variable]
  if (issue.code === 'invalid_type' && raw === undefined) {
    return 'is required but was not set'
  }
  return issue.message
}

/** Groepeert issues per variabele, in de volgorde waarin ze voorkomen. */
function groupIssues(issues: readonly RawIssue[], source: EnvSource): EnvProblem[] {
  const grouped = new Map<string, string[]>()

  for (const issue of issues) {
    const key = issue.path[0]
    const variable = key === undefined ? '(configuration)' : String(key)
    const message = describeIssue(issue, variable === '(configuration)' ? '' : variable, source)

    const existing = grouped.get(variable)
    if (existing === undefined) {
      grouped.set(variable, [message])
    } else if (!existing.includes(message)) {
      existing.push(message)
    }
  }

  return [...grouped.entries()].map(([variable, messages]) => ({ variable, messages }))
}

/**
 * Normaliseert de ruwe omgeving voordat er gevalideerd wordt.
 *
 * Een lege waarde in een .env-bestand (`API_PORT=`) is in de praktijk niet te
 * onderscheiden van een niet-gezette variabele. Zulke waarden worden verwijderd,
 * zodat de standaardwaarde geldt en een ontbrekende verplichte variabele als
 * ontbrekend wordt gemeld in plaats van als lege string. Omringende witruimte
 * verdwijnt, want die is bij het bewerken van een .env-bestand vrijwel altijd
 * per ongeluk ontstaan.
 */
function normalizeSource(source: EnvSource): EnvSource {
  const normalized: Record<string, string> = {}
  for (const [key, value] of Object.entries(source)) {
    if (value === undefined) {
      continue
    }
    const trimmed = value.trim()
    if (trimmed === '') {
      continue
    }
    normalized[key] = trimmed
  }
  return normalized
}

/**
 * Valideert de omgeving en levert een getypeerd object op.
 *
 * Werpt een `EnvValidationError` met een gegroepeerde, leesbare boodschap; de
 * rauwe zod-fout komt nooit naar buiten.
 */
export function loadEnv(source: EnvSource): Env {
  const normalized = normalizeSource(source)
  const result = envSchema.safeParse(normalized)
  if (result.success) {
    return result.data
  }
  throw new EnvValidationError(groupIssues(result.error.issues, normalized))
}
